package dev.lingvo.site.components.editor

import dev.lingvo.shared.MtProvider
import dev.lingvo.shared.PluralCategory
import dev.lingvo.shared.SourceSnapshotDto
import dev.lingvo.shared.TranslationDto
import dev.lingvo.shared.isCanonicalSourceReviewId
import dev.lingvo.shared.validateSourceSnapshot
import dev.lingvo.site.api.API_JSON
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.booleanOrNull
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import kotlinx.serialization.json.put
import kotlinx.serialization.json.putJsonObject
import org.w3c.dom.Storage
import kotlinx.browser.window

private const val DRAFT_STORAGE_PREFIX = "lingvo:translation-draft:v1"

internal data class StoredEditorDraft(
    val baseRevision: String?,
    val draft: EditorDraft,
)

internal enum class EditorDraftRecoveryDecision {
    RESTORE,
    CONFLICT,
    REMOVE,
}

internal fun decideEditorDraftRecovery(
    stored: StoredEditorDraft,
    current: EditorDraft,
    currentRevision: String?,
): EditorDraftRecoveryDecision = when {
    stored.draft.sameContentAs(current) && !stored.draft.sourceClaimCaptured && stored.draft.sourceSnapshot == null -> EditorDraftRecoveryDecision.REMOVE
    stored.baseRevision == currentRevision -> EditorDraftRecoveryDecision.RESTORE
    else -> EditorDraftRecoveryDecision.CONFLICT
}

private fun EditorDraft.sameContentAs(other: EditorDraft): Boolean =
    value == other.value && pluralForms == other.pluralForms

internal interface EditorDraftStore {
    fun load(projectSlug: String, locale: String, keyId: String): StoredEditorDraft?
    fun save(projectSlug: String, locale: String, keyId: String, draft: StoredEditorDraft)
    fun remove(projectSlug: String, locale: String, keyId: String)
}

internal object BrowserEditorDraftStore : EditorDraftStore {
    override fun load(projectSlug: String, locale: String, keyId: String): StoredEditorDraft? =
        runCatching {
            window.localStorage.getItem(storageKey(projectSlug, locale, keyId))
                ?.let(::decodeStoredEditorDraft)
        }.getOrNull()

    override fun save(
        projectSlug: String,
        locale: String,
        keyId: String,
        draft: StoredEditorDraft,
    ) {
        window.localStorage.safely {
            setItem(storageKey(projectSlug, locale, keyId), encodeStoredEditorDraft(draft))
        }
    }

    override fun remove(projectSlug: String, locale: String, keyId: String) {
        window.localStorage.safely { removeItem(storageKey(projectSlug, locale, keyId)) }
    }

    private fun Storage.safely(block: Storage.() -> Unit) {
        runCatching(block)
    }
}

private fun storageKey(projectSlug: String, locale: String, keyId: String): String =
    "$DRAFT_STORAGE_PREFIX:$projectSlug:$locale:$keyId"

internal fun encodeStoredEditorDraft(stored: StoredEditorDraft): String = buildJsonObject {
    stored.baseRevision?.let { put("baseRevision", it) }
    put("value", stored.draft.value)
    putJsonObject("pluralForms") {
        stored.draft.pluralForms.forEach { (category, value) -> put(category.name, value) }
    }
    stored.draft.provider?.let { put("provider", it.name) }
    put("unsafeSuggestion", stored.draft.unsafeSuggestion)
    stored.draft.sourceSnapshot?.let { put("sourceSnapshot", API_JSON.encodeToJsonElement(SourceSnapshotDto.serializer(), it)) }
    stored.draft.targetRevision?.let { put("targetRevision", it) }
    put("targetPresent", stored.draft.targetPresent)
    stored.draft.targetUpdatedAt?.let { put("targetUpdatedAt", it) }
    stored.draft.baseTarget?.let { put("baseTarget", API_JSON.encodeToJsonElement(TranslationDto.serializer(), it)) }
    put("sourceClaimCaptured", stored.draft.sourceClaimCaptured)
    stored.draft.textShape?.let { put("textShape", it.name) }
}.toString()

internal fun decodeStoredEditorDraft(value: String): StoredEditorDraft? = runCatching {
    val json = Json.parseToJsonElement(value).jsonObject
    val pluralForms = json["pluralForms"]
        ?.jsonObject
        ?.mapNotNull { (name, element) ->
            PluralCategory.entries.firstOrNull { it.name == name }
                ?.let { category -> category to element.jsonPrimitive.content }
        }
        ?.toMap()
        .orEmpty()
    val providerName = json["provider"]?.jsonPrimitive?.contentOrNull
    StoredEditorDraft(
        baseRevision = json["baseRevision"]?.jsonPrimitive?.contentOrNull,
        draft = EditorDraft(
            value = json["value"]?.jsonPrimitive?.contentOrNull.orEmpty(),
            pluralForms = pluralForms,
            dirty = true,
            saving = false,
            provider = providerName?.let { name -> MtProvider.entries.firstOrNull { it.name == name } },
            unsafeSuggestion = json["unsafeSuggestion"]?.jsonPrimitive?.booleanOrNull ?: false,
            sourceSnapshot = json["sourceSnapshot"]?.let {
                runCatching { API_JSON.decodeFromJsonElement(SourceSnapshotDto.serializer(), it) }.getOrNull()
                    ?.takeIf { snapshot -> validateSourceSnapshot(snapshot) == null }
            },
            targetRevision = json["targetRevision"]?.jsonPrimitive?.contentOrNull?.takeIf(::isCanonicalSourceReviewId),
            targetPresent = json["targetPresent"]?.jsonPrimitive?.booleanOrNull ?: false,
            targetUpdatedAt = json["targetUpdatedAt"]?.jsonPrimitive?.contentOrNull,
            baseTarget = json["baseTarget"]?.let { runCatching {
                API_JSON.decodeFromJsonElement(TranslationDto.serializer(), it)
            }.getOrNull() },
            sourceClaimCaptured = json["sourceClaimCaptured"]?.jsonPrimitive?.booleanOrNull == true || "sourceSnapshot" in json,
            textShape = json["textShape"]?.jsonPrimitive?.contentOrNull?.let { name -> EditorDraftShape.entries.firstOrNull { it.name == name } },
        ),
    )
}.getOrNull()
